import { type UseQueryResult } from "@tanstack/react-query";
import { type parameter_payloads as ddcParameterPayloads } from "ddc";
import { createContext } from "react";
import { type MediaDataRoomJobHookResultWithoutCacheSetter } from "features/mediaDataRoom/hooks";
import {
  type AdvertiserAudienceV3,
  type Audience,
  type AudienceAb,
  type AudienceV3,
  type LookalikeAudience,
  type MediaDataRoomParticipant,
  type MediaDataRoomVersion,
  type PublishedDatasetsHashes,
  type RuleBasedAudience,
  type SeedAudienceV3,
} from "features/mediaDataRoom/models";
import { type AudienceReference, type CustomAudience } from "./v3/models";

export class AudienceSizeEstimationError extends Error {
  constructor(
    message: string,
    public readonly audienceId: string,
    public readonly originalError: unknown
  ) {
    super(message);
    this.name = "AudienceSizeEstimationError";
    Object.setPrototypeOf(this, AudienceSizeEstimationError.prototype);
  }
}

export interface DisableSizeEstimationForAudienceFn {
  (audienceId: string): void;
  (audienceIds: string[]): void;
}

export interface AudienceSizesStateHookResult {
  enableSizeEstimationForAudience: (audienceId: string) => void;
  audienceSizes: Record<
    string,
    { audienceSize: number | null; loading: boolean; error: Error | null }
  >;
  disableSizeEstimationForAudience: DisableSizeEstimationForAudienceFn;
}

export type AudienceSizesQueryResult = UseQueryResult<
  { [id: string]: number },
  Error
>[];

type AudiencesResultWithoutCacheSetter<A = Audience> =
  MediaDataRoomJobHookResultWithoutCacheSetter<A[]>;

export type AudienceSizesResult = AudienceSizesStateHookResult & {
  retrySizeEstimationForAudience: (audienceId: string) => void;
};

// TODO: We probably gonna need to rename it to something more agnostic since for V3 its not advertiser only audiences
export interface AdvertiserAudiencesContextValue {
  audiences: AudiencesResultWithoutCacheSetter;
  downloadAudience: (audienceId: string) => Promise<void>;
  exportAudienceAsDataset: (audienceId: string) => Promise<void>;
  isExportingAudience: Record<string, boolean>;
  deleteAudience: (audienceId: string) => Promise<void>;
  isDeletingAudience: boolean;
  audienceSizes: AudienceSizesResult;
  isSavingAudience: boolean;
  doesAudienceDependOnLookalikeAudience: (audienceId: string) => boolean;
  getAudiencePreqrequisites: (
    audienceId: string
  ) => Promise<AudienceV3[] | undefined> | (AudienceAb[] | undefined);
  version: MediaDataRoomVersion;
}

export interface AdvertiserAudiencesAbContextValue
  extends Omit<AdvertiserAudiencesContextValue, "audiences"> {
  audiences: AudiencesResultWithoutCacheSetter<AudienceAb>;
  saveAudience: (
    audience: LookalikeAudience | RuleBasedAudience
  ) => Promise<void>;
  isAudiencesDataOutdated: boolean;
  canResetAudiences: boolean;
  resetAudiences: (hashes: PublishedDatasetsHashes) => Promise<void>;
  getParameterPayloads: (
    audienceId: string,
    defaultAudiences?: AudienceAb[]
  ) => ddcParameterPayloads.ParameterPayloads;
  doesAudienceDependOnLookalikeAudience: (
    audienceId: string,
    defaultAudiences?: AudienceAb[]
  ) => boolean;
  publishAudience: (audienceId: string) => Promise<void>;
  isPublishingAudience: boolean;
}

interface PublisherAudiencesCommonContextValue
  extends Pick<
      AdvertiserAudiencesContextValue,
      | "downloadAudience"
      | "exportAudienceAsDataset"
      | "isExportingAudience"
      | "audienceSizes"
    >,
    Pick<
      AdvertiserAudiencesAbContextValue,
      | "isAudiencesDataOutdated"
      | "canResetAudiences"
      | "resetAudiences"
      | "getParameterPayloads"
      | "doesAudienceDependOnLookalikeAudience"
    > {
  version: MediaDataRoomVersion;
}

export type PublisherAudiencesContextValue =
  PublisherAudiencesCommonContextValue & {
    audiences: AudiencesResultWithoutCacheSetter;
  };
export type PublisherAudiencesAbContextValue =
  PublisherAudiencesCommonContextValue & {
    audiences: AudiencesResultWithoutCacheSetter<AudienceAb>;
  };

export interface AudienceByIdSelectorFn {
  (audienceId: string, includeSeedAudiences?: false): AudienceV3;
  (audienceId: string, includeSeedAudiences: true): AudienceV3 | SeedAudienceV3;
}

export interface AdvertiserAudiencesV3ContextValue
  extends Omit<AdvertiserAudiencesContextValue, "audiences"> {
  audiences: AudiencesResultWithoutCacheSetter<AudienceV3>;
  saveAudience: (
    audience: RuleBasedAudience | LookalikeAudience | AdvertiserAudienceV3
  ) => Promise<void>;
  seedAudiences: AudiencesResultWithoutCacheSetter<SeedAudienceV3>;
  shareAudience: (
    audienceId: string,
    participantGroups: MediaDataRoomParticipant[]
  ) => Promise<AudienceV3 | null>;
  isSharingAudience: boolean;
  getRawAudienceRef: (
    audience: AudienceV3 | SeedAudienceV3
  ) => AudienceReference;
  mapAudienceToDraftCustomAudience: (
    audience: RuleBasedAudience | LookalikeAudience | AdvertiserAudienceV3
  ) => CustomAudience;
  canCurrentUserDeleteAudience: (audience: AudienceV3) => boolean;
  canCurrentUserShareAudience: (audience: AudienceV3) => boolean;
  getAudienceById: AudienceByIdSelectorFn;
}

export interface AdvertiserAudiencesContextHook {
  (version?: never): AdvertiserAudiencesContextValue;
  (version: MediaDataRoomVersion.v3): AdvertiserAudiencesV3ContextValue;
  (version: MediaDataRoomVersion.ab): AdvertiserAudiencesAbContextValue;
}

export const AdvertiserAudiencesContext =
  createContext<AdvertiserAudiencesContextValue | null>(null);
AdvertiserAudiencesContext.displayName = "AdvertiserAudiencesContext";

export const AdvertiserAudiencesContextConsumer =
  AdvertiserAudiencesContext.Consumer;
